import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentResult() {
  const location = useLocation();
  const isSuccess = location.pathname.includes("success");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(isSuccess ? "checking" : "cancelled");
  const tries = useRef(0);

  useEffect(() => {
    if (!isSuccess) return;
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current < 20) setTimeout(poll, 2000);
      else setStatus("error");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <div data-testid="payment-result-page" className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl border-2 border-foreground/10 p-8 text-center">
        {status === "checking" && (
          <>
            <p data-testid="payment-status-message" className="text-2xl font-extrabold">Confirming your payment…</p>
            <p className="mt-2 text-lg text-muted-foreground">This takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="mx-auto w-20 h-20 rounded-full bg-[#48BB78]/15 grid place-items-center">
              <Check size={44} strokeWidth={3} className="text-[#2F855A]" />
            </span>
            <p data-testid="payment-status-message" className="mt-4 text-2xl font-extrabold">Welcome to Premium!</p>
            <p className="mt-2 text-lg text-muted-foreground">Everything is now unlocked on your account.</p>
            <Link to="/app" data-testid="go-to-dashboard-button" className="mt-6 min-h-[56px] rounded-full bg-primary text-white font-bold text-lg flex items-center justify-center">
              Go to my dashboard
            </Link>
          </>
        )}
        {status === "cancelled" && (
          <>
            <span className="mx-auto w-20 h-20 rounded-full bg-secondary grid place-items-center">
              <X size={44} strokeWidth={3} className="text-muted-foreground" />
            </span>
            <p data-testid="payment-status-message" className="mt-4 text-2xl font-extrabold">Payment cancelled</p>
            <p className="mt-2 text-lg text-muted-foreground">No charge was made. You're still on the free plan.</p>
            <Link to="/premium" data-testid="back-to-premium-button" className="mt-6 min-h-[56px] rounded-full bg-primary text-white font-bold text-lg flex items-center justify-center">
              Back to Premium
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <p data-testid="payment-status-message" className="text-2xl font-extrabold">We couldn't confirm the payment yet</p>
            <p className="mt-2 text-lg text-muted-foreground">If you completed checkout, your upgrade will appear shortly.</p>
            <Link to="/app" data-testid="go-to-dashboard-button" className="mt-6 min-h-[56px] rounded-full bg-primary text-white font-bold text-lg flex items-center justify-center">
              Go to my dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
